import { useHandbook, useHandbookKeys } from '~/controllers/handbook';
import {
  ARRAY_GROUP_SOURCES,
  FIELD_LABELS,
  HANDBOOK_FIELD_KEYS,
} from '~/constants/agentReview';

/** Maps uploaded contract file(s) to original file names for the activity log API. */
export const toAgentContractFileNames = (files: unknown): string[] => {
  if (files == null) return [];

  const list = Array.isArray(files) ? files : [files];

  return list
    .map((file) => {
      if (typeof file === 'string') return file;
      if (file && typeof file === 'object' && 'originalName' in file) {
        return (file as { originalName?: string }).originalName;
      }
      return null;
    })
    .filter((name): name is string => Boolean(name));
};

export const lookupMccLabel = (
  handbook: ReturnType<typeof useHandbook>['data'],
  apiValue: string
): string | null => {
  const leaves = handbook?.handbooks
    ?.find((h: any) => h.key === 'mcc')
    ?.values?.flatMap((g: any) => g.values ?? [])
    ?.flatMap((sg: any) => sg.values ?? []);
  const match = leaves?.find((v: any) => v.apiValue === apiValue);
  return match ? `${match.localizedValue} (${match.apiValue})` : null;
};

export const localizeValue = (
  rawPath: string,
  value: string | null,
  handbook: ReturnType<typeof useHandbook>['data'],
  handbookKeys: ReturnType<typeof useHandbookKeys>
): string | null => {
  if (value == null || typeof value !== 'string') return value;
  const stripped = rawPath
    .replace(/^review[A-Z][A-Za-z]*\./, '')
    .replace(/^([a-zA-Z]+)\[\d+\]\./, '');

  if (stripped === 'agentContract') {
    if (value === '[]' || value === 'null' || value === '') return null;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const names = parsed
          .map((item) => (typeof item === 'string' ? item : item?.originalName))
          .filter(Boolean);
        return names.length ? (names as string[]).join(', ') : null;
      }
      if (parsed && typeof parsed === 'object' && parsed.originalName) {
        return parsed.originalName as string;
      }
    } catch {
      // fall through to return raw value
    }
  }

  if (stripped === 'companyInformation.mccCode') {
    return lookupMccLabel(handbook, value) ?? value;
  }

  const handbookKind = HANDBOOK_FIELD_KEYS[stripped];
  if (!handbookKind) return value;
  const handbookKey = handbookKeys[handbookKind];
  const localized = handbook?.handbooks
    ?.find((h: any) => h.key === handbookKey)
    ?.values?.find((v: any) => v.apiValue === value)?.localizedValue;
  return localized || value;
};

export const getArrayItemIdentifier = (
  payload: any,
  group: string,
  index: number
): string | null => {
  const list = ARRAY_GROUP_SOURCES[group]?.(payload) ?? null;
  const item = Array.isArray(list) ? list[index] : null;
  if (!item) return null;
  return (
    item?.personalDocument?.idNumber || item?.passport?.documentNumber || item?.crNumber || null
  );
};

export const formatFieldName = (fieldName: string, payload?: any): string => {
  const stripped = fieldName.replace(/^review[A-Z][A-Za-z]*\./, '');
  const arrayMatch = stripped.match(/^([a-zA-Z]+)\[(\d+)\]\.(.+)$/);
  if (arrayMatch) {
    const [, group, idxStr, rest] = arrayMatch;
    const idx = Number(idxStr);
    const label = FIELD_LABELS[rest] ?? rest;
    const identifier = getArrayItemIdentifier(payload, group, idx);
    const groupLabel = identifier ? `ID ${identifier}` : `${group} #${idx + 1}`;
    return `${groupLabel} › ${label}`;
  }
  return FIELD_LABELS[stripped] ?? stripped;
};
